// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import { FORMID, TestRunParams } from "components/TestRun/TestRunConstants";
import {
  FedExError,
  FedExPickUpAddress,
  FedExShipperDetails,
} from "./FedExTypes";
import React, { useCallback, useEffect, useState } from "react";
import {
  capitalizeWords,
  phoneNumberFormatter,
  zipCodeFormatter,
} from "components/ui/Questionnaire/QuestionnaireFormatters";
import useFedExFetchApi, {
  FedExValidateAddressResponse,
} from "./useFedExFetchApi";
import {
  validateName,
  validatePhoneNumber,
  validateZipCode,
} from "components/ui/Questionnaire/validators/QuestionnaireValidators";

import { ErrorTypes } from "utils/useDataFetchApi";
import Grid from "@material-ui/core/Grid";
import LoadingOverlay from "components/ui/LoadingOverlay";
import NextStepButton from "components/TestRun/NextStepButton";
import Paragraph from "components/ui/Paragraph";
import { Question } from "components/ui/Questionnaire/QuestionnaireTypes";
import Questionnaire from "components/ui/Questionnaire/Questionnaire";
import QuestionnaireInput from "components/ui/Questionnaire/QuestionnaireInput";
import TestRunStepLink from "components/TestRun/TestRunStepLink";
import Title from "components/ui/Title";
import { confirmAlert } from "utils/confirmAlert";
import { setStepReady } from "components/TestRun/TestRunStepReadyEmitter";
import { useParams } from "react-router-dom";
import { useTestRunState } from "store/testrun";
import { useTheme } from "@material-ui/core";

interface FedExAddressFormProps {
  initialAddress?: FedExPickUpAddress;
  initialShipperDetails?: FedExShipperDetails;
  addressError?: FedExError;
  onAddressValidated: (data: {
    originalAddress: FedExPickUpAddress;
    normalizedAddress: FedExPickUpAddress;
    shipperDetails: FedExShipperDetails;
  }) => void;
}

export default (props: FedExAddressFormProps) => {
  const { addressError, initialAddress, initialShipperDetails } = props;
  const { step } = useParams<TestRunParams>();
  const theme = useTheme();
  const [fetchState, { validateAddress }] = useFedExFetchApi();
  const testRunState = useTestRunState();

  const [shipperDetails, setShipperDetails] = useState<
    FedExShipperDetails | undefined
  >();
  const [originalAddress, setOriginalAddress] = useState<FedExPickUpAddress>();

  const stepReadyCallback = useCallback(
    (stepReady: boolean) => {
      setStepReady(step, stepReady);
    },
    [step]
  );

  useEffect(() => {
    if (fetchState.isLoading) {
      setStepReady(step, false);
    } else if (fetchState.isError) {
      switch (fetchState.errorType) {
        case ErrorTypes.NETWORK_ERROR:
          confirmAlert({
            message:
              "A network error has occured. Please check your connection and try again.",
            theme,
          });
          break;
        default:
          // If address validation is otherwise unavailable, just continue on
          props.onAddressValidated({
            originalAddress: originalAddress!,
            normalizedAddress: originalAddress!,
            shipperDetails: shipperDetails!,
          });
      }
    } else if (fetchState.data) {
      const result = fetchState.data.result as FedExValidateAddressResponse;
      props.onAddressValidated({
        originalAddress: originalAddress!,
        normalizedAddress: result.normalizedAddress,
        shipperDetails: shipperDetails!,
      });
    }
  }, [
    fetchState.data,
    fetchState.isError,
    fetchState.isLoading,
    props,
    shipperDetails,
    step,
    originalAddress,
    fetchState.errorType,
    theme,
  ]);

  const addressLineFormatter = (input: string) => {
    return input
      .trim()
      .replace(/\s{2,}/g, " ") // redundant whitespace
      .replace(/\./g, ""); // periods
  };

  const registrationToken = testRunState.testRun?.registrationToken;
  const onSubmit = useCallback(
    (event: React.FormEvent, questions: { [key: string]: Question }) => {
      event.preventDefault();

      const line = [addressLineFormatter(questions.address1.value!)];
      if (questions.address2.value) {
        line.push(addressLineFormatter(questions.address2.value));
      }

      const city = questions.city.value!.trim().replace(/\s{2,}/g, " ");

      const address = {
        line: line,
        city: city,
        state: questions.state.value!,
        postalCode: questions.zipcode.value!,
        country: "US",
      };

      validateAddress({
        address,
        registrationToken: registrationToken!,
      });

      setShipperDetails({
        name: questions.name.value!,
        phone: questions.phone.value!,
      });
      setOriginalAddress(address);
    },
    [validateAddress, registrationToken]
  );

  useEffect(() => {
    switch (addressError) {
      case FedExError.NETWORK_ERROR:
        confirmAlert({
          message:
            "A network error has occured. Please check your connection and try again.",
          theme,
        });
        break;
      case FedExError.NO_SERVICE:
        confirmAlert({
          message:
            "FedEx does not offer package pickup at this address. Please enter a different address or call to schedule a pickup.",
          theme,
        });
        break;
      case FedExError.SCHEDULE_PICKUP_FAILED:
        confirmAlert({
          message:
            "There was an error scheduling a pickup with FedEx, please try again.",
          theme,
        });
        break;
    }
  }, [addressError, theme]);

  return (
    <>
      <Title>Schedule a pickup</Title>
      <Paragraph>
        Schedule FedEx to pick up the kit from your location or{" "}
        <TestRunStepLink nav="callToSchedule">
          call to schedule a pickup
        </TestRunStepLink>{" "}
        over the phone.
      </Paragraph>
      <Paragraph>
        Confirm pickup is available from your location by entering your address
        information below.
      </Paragraph>
      <Paragraph>All fields with an * are required.</Paragraph>
      <Questionnaire
        formId={FORMID}
        onFormReady={stepReadyCallback}
        onSubmit={onSubmit}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <QuestionnaireInput
              label="Contact name:"
              mandatory={true}
              name="name"
              type="text"
              validators={[validateName]}
              formatter={capitalizeWords}
              value={initialShipperDetails?.name}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <QuestionnaireInput
              label="Street address 1:"
              mandatory={true}
              name="address1"
              type="text"
              validators={[validateName]}
              formatter={capitalizeWords}
              value={initialAddress?.line[0]}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <QuestionnaireInput
              label="Street address 2:"
              mandatory={false}
              name="address2"
              placeholder="(e.g. Suite, unit, floor)"
              type="text"
              validators={[validateName]}
              formatter={capitalizeWords}
              value={initialAddress?.line[1]}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <QuestionnaireInput
              label="City:"
              mandatory={true}
              name="city"
              type="text"
              formatter={capitalizeWords}
              value={initialAddress?.city}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <QuestionnaireInput
              label=""
              mandatory={true}
              name="state"
              placeholder="State"
              autofill="region"
              native={true}
              selectOptions={[
                // { value: "AK", name: "AK" },
                { value: "AL", name: "AL" },
                { value: "AR", name: "AR" },
                { value: "AZ", name: "AZ" },
                { value: "CA", name: "CA" },
                { value: "CO", name: "CO" },
                { value: "CT", name: "CT" },
                { value: "DC", name: "DC" },
                { value: "DE", name: "DE" },
                { value: "FL", name: "FL" },
                { value: "GA", name: "GA" },
                // { value: "HI", name: "HI" },
                { value: "IA", name: "IA" },
                { value: "ID", name: "ID" },
                { value: "IL", name: "IL" },
                { value: "IN", name: "IN" },
                { value: "KS", name: "KS" },
                { value: "KY", name: "KY" },
                { value: "LA", name: "LA" },
                { value: "MA", name: "MA" },
                { value: "MD", name: "MD" },
                { value: "ME", name: "ME" },
                { value: "MI", name: "MI" },
                { value: "MN", name: "MN" },
                { value: "MO", name: "MO" },
                { value: "MS", name: "MS" },
                { value: "MT", name: "MT" },
                { value: "NC", name: "NC" },
                { value: "ND", name: "ND" },
                { value: "NE", name: "NE" },
                { value: "NH", name: "NH" },
                { value: "NJ", name: "NJ" },
                { value: "NM", name: "NM" },
                { value: "NV", name: "NV" },
                { value: "NY", name: "NY" },
                { value: "OH", name: "OH" },
                { value: "OK", name: "OK" },
                { value: "OR", name: "OR" },
                { value: "PA", name: "PA" },
                { value: "RI", name: "RI" },
                { value: "SC", name: "SC" },
                { value: "SD", name: "SD" },
                { value: "TN", name: "TN" },
                { value: "TX", name: "TX" },
                { value: "UT", name: "UT" },
                { value: "VA", name: "VA" },
                { value: "VT", name: "VT" },
                { value: "WA", name: "WA" },
                { value: "WI", name: "WI" },
                { value: "WV", name: "WV" },
                { value: "WY", name: "WY" },
                // { value: "AS", name: "AS" },
                // { value: "FM", name: "FM" },
                // { value: "GU", name: "GU" },
                // { value: "MH", name: "MH" },
                // { value: "MP", name: "MP" },
                // { value: "PR", name: "PR" },
                // { value: "PW", name: "PW" },
                // { value: "VI", name: "VI" },
              ]}
              type="select"
              value={initialAddress?.state}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <QuestionnaireInput
              formatter={zipCodeFormatter}
              label="ZIP:"
              mandatory={true}
              name="zipcode"
              type="text"
              validators={[validateZipCode]}
              value={initialAddress?.postalCode}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <QuestionnaireInput
              formatter={phoneNumberFormatter}
              label="Phone number:"
              mandatory={true}
              name="phone"
              type="tel"
              validators={[validatePhoneNumber]}
              value={initialShipperDetails?.phone}
            />
          </Grid>
        </Grid>
      </Questionnaire>
      <NextStepButton text="Confirm availability" />
      <LoadingOverlay isOpen={fetchState.isLoading} title="Verifying Address" />
    </>
  );
};
